// @file For frame previews on tldraw canvas
// @file For whiteboard frame previews -- used in frame bar and linking panel

import environment from '@@/bits/environment'
import { isAppUsing } from '@@/bits/flip'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { unboundedWatch } from '@@/bits/vue'
import { BackgroundType, isShapeApproved } from '@@/bits/whiteboard'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import {
  createDebouncedHandlePageChange,
  getChangedIds,
  getMinifiedSvgStringForShapes,
  getSvgStringForShapes,
  isEditorIdle,
} from '@@/bits/whiteboard_preview_utils'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { processedUrl } from '@padlet/vivaldi-client'
import { uniq } from 'es-toolkit'
import { defineStore } from 'pinia'
import { Box, type HistoryEntry, type TLPageId, type TLRecord, type TLShapeId } from 'tldraw'
import { ref } from 'vue'

// TODO: Replace with url to 1px blank image to increase performance
export const BLANK_FRAME_BASE64 = 'data:image/gif;base64,R0lGODlhAQABAIAAAP7//wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw=='
export const CANVAS_CARD_PREVIEW_PADDING = -4
const EMPTY_SVG = `<div></div>`
const SVG_TO_PNG_WORKER_BASE_URL = 'https://padlet.pics/1/cards/'
const SVG_TO_PNG_WORKER_BASE_URL_SUFFIX = '.png'
// const SVG_TO_PNG_WORKER_BASE_URL = 'https://svg-png-d044.padlet.workers.dev/'
// const SVG_TO_PNG_WORKER_BASE_URL_SUFFIX = ''

async function getSvgStringFromShapeIds(shapeIds: TLShapeId[]): Promise<string> {
  const editor = getEditorObject()
  if (editor == null || shapeIds.length === 0) {
    return EMPTY_SVG
  }
  const approvedShapeIds = shapeIds.filter((shapeId) => isShapeApproved(editor.getShape(shapeId)))
  const svg = await getSvgStringForShapes(approvedShapeIds, {
    bounds: new Box(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT),
    preserveAspectRatio: 'xMidYMin slice',
    padding: CANVAS_CARD_PREVIEW_PADDING,
  })
  return svg ?? EMPTY_SVG
}

async function getMinifiedSvgStringFromShapeIds(shapeIds: TLShapeId[]): Promise<string> {
  const editor = getEditorObject()
  if (editor == null || shapeIds.length === 0) {
    return EMPTY_SVG
  }
  const approvedShapeIds = shapeIds.filter((shapeId) => isShapeApproved(editor.getShape(shapeId)))
  const svg = await getMinifiedSvgStringForShapes(approvedShapeIds, {
    bounds: new Box(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT),
    preserveAspectRatio: 'xMidYMin slice',
    padding: -4,
  })
  return svg ?? EMPTY_SVG
}

export type FramePreview =
  | {
      type: 'svg'
      svgTag: string
    }
  | {
      type: 'png'
      svgTag: string
      backgroundTag: string
      png: string
    }

export const useWhiteboardFramePreviewStore = defineStore('whiteboardFramePreview', () => {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
  const surfaceStore = useSurfaceStore()

  /**
   * FRAME LIVE PREVIEW
   */

  const framePreviews = ref<Record<string, FramePreview>>({})
  const framesWithPreviewChanges = ref<string[]>([])

  // Helpers
  function getSvgStringFromFrameBackgroundOld(pageId: TLPageId): string {
    const editor = getEditorObject()
    if (editor == null) {
      return EMPTY_SVG
    }
    const page = editor.getPage(pageId)
    if (page == null) {
      return EMPTY_SVG
    }
    const meta = page.meta
    const backgroundType =
      whiteboardFrameBackgroundStore.previewFrameBackgrounds[pageId]?.id ?? (meta.backgroundType as BackgroundType)
    const backgroundImageUrl =
      whiteboardFrameBackgroundStore.displayedFrameBackgrounds?.[pageId]?.url ??
      whiteboardFrameBackgroundStore.getPageBackgroundUrl(page)
    let backgroundStyleString = ''
    if (backgroundType === BackgroundType.Custom) {
      backgroundStyleString = `background-repeat: no-repeat; background-size: cover;`
    } else {
      backgroundStyleString = `background-repeat: repeat; background-size: contain;`
    }
    return `<div style="position: absolute; inset: 0; z-index: -1; background-image: url(${processedUrl(
      backgroundImageUrl,
      {
        // The dimensions of the frame preview on tablets and mobiles
        width: 96,
        height: 54,
      },
    )}); ${backgroundStyleString}"></div>`
  }

  function getSvgStringFromFrameBackground(pageId: TLPageId): string {
    const editor = getEditorObject()
    if (editor == null) {
      return EMPTY_SVG
    }
    const page = editor.getPage(pageId)
    if (page == null) {
      return EMPTY_SVG
    }
    const backgroundImageUrl =
      whiteboardFrameBackgroundStore.displayedFrameBackgrounds?.[pageId]?.thumbnailUrl ??
      whiteboardFrameBackgroundStore.getPageBackgroundUrl(page)

    return `<img alt="" src="${processedUrl(backgroundImageUrl, {
      width: 78,
      height: 44,
    })}" width="78" height="44" style="width: 100%; height: 100%;"/>`
  }

  function getPngPathFromPageId(pageId: TLPageId): string {
    const cacheKey = getCacheKeyForFramePreview(pageId)
    const fullCacheKey = `${environment}-${cacheKey}`
    return `${SVG_TO_PNG_WORKER_BASE_URL}${fullCacheKey}${SVG_TO_PNG_WORKER_BASE_URL_SUFFIX}`
  }

  function getCacheKeyForFramePreview(pageId: TLPageId): string {
    const editor = getEditorObject()
    if (editor == null) {
      return ''
    }
    const pageLastModified = editor.getPage(pageId)?.meta.lastModifiedAt as number | undefined
    return `${surfaceStore.wallId as string}-${pageId}-${pageLastModified ?? '0'}`
  }

  async function updatePreviewForSingleFrame(specificPageId: TLPageId): Promise<void> {
    const editor = getEditorObject()
    if (editor == null) return

    const frame = whiteboardStore.frames.find((frame) => frame.id === specificPageId)
    if (frame == null) return

    const shapeIds = Array.from(editor.getPageShapeIds(specificPageId))
    const svgTag = isAppUsing('sandboxPngFramePreview')
      ? await getMinifiedSvgStringFromShapeIds(shapeIds)
      : await getSvgStringFromShapeIds(shapeIds)

    let frameBgSvg = getSvgStringFromFrameBackgroundOld(specificPageId)

    if (isAppUsing('sandboxCardNavigation')) {
      frameBgSvg = getSvgStringFromFrameBackground(specificPageId)
    }

    const preview: FramePreview =
      svgTag === EMPTY_SVG
        ? {
            type: 'svg',
            svgTag: `${frameBgSvg}${svgTag}`,
          }
        : isAppUsing('sandboxPngFramePreview')
        ? {
            type: 'png',
            svgTag,
            backgroundTag: frameBgSvg,
            png: getPngPathFromPageId(specificPageId),
          }
        : {
            type: 'svg',
            svgTag: `${frameBgSvg}${svgTag}`,
          }

    framePreviews.value = {
      ...framePreviews.value,
      [specificPageId]: preview,
    }
  }

  async function updatePreviewForAllFrames(specificPageId?: TLPageId): Promise<void> {
    const editor = getEditorObject()
    if (editor == null) return
    const pages = editor.getPages()
    if (specificPageId != null) {
      void updatePreviewForSingleFrame(specificPageId)
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    pages.forEach(async (page) => {
      await updatePreviewForSingleFrame(page.id)
    })
  }

  // Listen to frame content changes and track which frames have changes
  function handlePageChange(event: HistoryEntry<TLRecord>): void {
    const pagesWithChange = getChangedIds(event, 'page')
    if (pagesWithChange.size === 0) return
    framesWithPreviewChanges.value = uniq([...framesWithPreviewChanges.value, ...Array.from(pagesWithChange)])
  }
  const debouncedHandlePageChange = createDebouncedHandlePageChange(handlePageChange)
  // When there is a frame with preview changes, run the actual logic to update the preview for the frames

  function conditionallyUpdateFramesWithChanges(): void {
    if (framesWithPreviewChanges.value.length === 0 || !isEditorIdle()) {
      return
    }
    framesWithPreviewChanges.value.forEach((pageId) => {
      void updatePreviewForSingleFrame(pageId as TLPageId)
    })
    framesWithPreviewChanges.value = []
  }

  void unboundedWatch(framesWithPreviewChanges, conditionallyUpdateFramesWithChanges)

  // When there is a new frame, add a blank frame preview
  void unboundedWatch(
    () => whiteboardStore.frames,
    (newFrameList, oldFrameList) => {
      const addedFrames = newFrameList.filter(
        (frame) => oldFrameList.find((oldFrame) => oldFrame.id === frame.id) == null,
      )
      if (addedFrames.length > 0) {
        framePreviews[addedFrames[0].id] = {
          type: 'svg',
          svgTag: EMPTY_SVG,
        }
      }
    },
  )

  return {
    // State
    framePreviews,
    framesWithPreviewChanges,
    conditionallyUpdateFramesWithChanges,
    getSvgStringFromFrameBackground,

    // Actions
    updatePreviewForSingleFrame,
    updatePreviewForAllFrames,
    debouncedHandlePageChange,
  }
})
